import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
    collection,
    addDoc,
    Timestamp,
    query,
    orderBy,
    onSnapshot,
    getDoc,
    deleteDoc,
    doc,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../firebase";

import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Stack,
} from "@mui/material";

import { Edit, Delete } from "@mui/icons-material";

export default function ExpenseHead() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
    });

    const [user, setUser] = useState(null);
    const [propertyId, setPropertyId] = useState(null);
    const [expenseHead, setExpenseHead] = useState([]);
    const [searchText, setSearchText] = useState("");

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    const [headname, setheadname] = useState("");
    const [editId, setEditId] = useState("");
    const [editheadname, setEditheadname] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    /* ================= FETCH PROPERTY ================= */

    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (u) => {
            if (u) {
                setUser(u);
                const docRef = doc(db, "authroles", u.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setPropertyId(docSnap.data().propertyId);
                }
            }
        });

        return () => unsub();
    }, []);

    /* ================= FETCH EXPENSE HEAD ================= */

    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, "expensehead"),
            where("propertyId", "==", propertyId),
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setExpenseHead(
                snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }))
            );
        });

        return () => unsubscribe();
    }, [propertyId]);

    /* ================= ADD ================= */

    const handleAdd = async () => {
        if (!headname) {
            Toast.fire({ icon: "error", title: "Enter Expense Head" });
            return;
        }

        await addDoc(collection(db, "expensehead"), {
            headname,
            propertyId,
            uid: user.uid,
            createdAt: Timestamp.now(),
        });

        setheadname("");
        setOpenAdd(false);

        Toast.fire({ icon: "success", title: "Added Successfully" });
    };

    /* ================= DELETE ================= */

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;

        await deleteDoc(doc(db, "expensehead", id));
        Toast.fire({ icon: "success", title: "Deleted Successfully" });
    };

    /* ================= UPDATE ================= */

    const handleUpdate = async () => {
        if (!editheadname) {
            Toast.fire({ icon: "error", title: "Enter Expense Head" });
            return;
        }

        await updateDoc(doc(db, "expensehead", editId), {
            headname: editheadname,
        });

        setOpenEdit(false);
        Toast.fire({ icon: "success", title: "Updated Successfully" });
    };

    /* ================= FILTER ================= */

    const filteredData = expenseHead.filter((item) =>
        item.headname?.toLowerCase().includes(searchText.toLowerCase())
    );

    /* ================= UI ================= */

    return (
        <Box className="content-wrapper" p={3}>
            <Card>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" mb={2}>
                        <Typography variant="h6">Expense Head Management</Typography>

                        <Button variant="contained" onClick={() => setOpenAdd(true)}>
                            Add Expense Head
                        </Button>
                    </Stack>

                    <TextField
                        size="small"
                        label="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sr No</TableCell>
                                    <TableCell>Expense Head</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => (
                                        <TableRow key={row.id}>
                                            <TableCell>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>
                                            <TableCell>{row.headname}</TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => {
                                                        setEditId(row.id);
                                                        setEditheadname(row.headname);
                                                        setOpenEdit(true);
                                                    }}
                                                >
                                                    <Edit />
                                                </IconButton>

                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDelete(row.id)}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredData.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </CardContent>
            </Card>

            {/* ================= ADD DIALOG ================= */}

            <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
                <DialogTitle>Add Expense Head</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth size="small"
                        margin="dense"
                        label="Expense Head"
                        value={headname}
                        onChange={(e) => setheadname(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAdd}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ================= EDIT DIALOG ================= */}

            <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
                <DialogTitle>Edit Expense Head</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth size="small"
                        margin="dense"
                        label="Expense Head"
                        value={editheadname}
                        onChange={(e) => setEditheadname(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdate}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}